import { parseGtmValue } from "./gtm"
import { normalizeUrl } from "./security"

const GA4_MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]{4,}$/i

export const formatCrossDomainActionMessage = (hostname) =>
  `Add ${hostname} to the cross-domain list in GA4.`

export const normalizeHostnameText = (value) => {
  if (typeof value !== "string") return ""
  const trimmed = value.trim().toLowerCase()
  if (!trimmed) return ""
  return trimmed
    .replace(/^https?:\/\//, "")
    .replace(/^[^/]*@/, "")
    .replace(/[:/].*$/, "")
}

const extractGaContainerDataSection = (script) => {
  if (typeof script !== "string") return ""
  const start = script.indexOf("var data =")
  if (start === -1) return ""
  let idx = script.indexOf("{", start)
  if (idx === -1) return ""
  let depth = 1
  idx += 1
  while (idx < script.length && depth > 0) {
    const ch = script[idx]
    if (ch === "{") depth += 1
    else if (ch === "}") depth -= 1
    idx += 1
  }
  if (idx < script.length && script[idx] === ";") idx += 1
  return script.slice(start, idx)
}

const normalizeGtmCollection = (items) => {
  if (!Array.isArray(items)) return []
  return items.map((item) => {
    const normalized = parseGtmValue(item)
    if (
      normalized &&
      typeof normalized === "object" &&
      !Array.isArray(normalized)
    ) {
      return normalized
    }
    return { value: normalized }
  })
}

const parseGaContainerContents = (contents) => {
  if (typeof contents !== "string") return null
  const trimmed = contents.trim()
  if (!trimmed) return null
  const snippet = extractGaContainerDataSection(trimmed)
  const payload = snippet || trimmed
  const jsonText = payload
    .replace(/^var\s+data\s*=\s*/, "")
    .replace(/;\s*$/, "")
  try {
    const obj = JSON.parse(jsonText)
    const resource = obj.resource || {}
    return {
      tags: normalizeGtmCollection(resource.tags || resource.tag),
      macros: normalizeGtmCollection(resource.macros || resource.macro),
      predicates: normalizeGtmCollection(resource.predicates || resource.predicate),
      rules: normalizeGtmCollection(resource.rules || resource.rule),
    }
  } catch (err) {
    console.warn("[GA4] Failed to parse GA4 container contents", err)
    return null
  }
}

const extractCrossDomainPatternValues = (rawValue) => {
  if (!Array.isArray(rawValue)) return []
  const values = rawValue[0] === "list" ? rawValue.slice(1) : rawValue
  return values.filter((item) => typeof item === "string")
}

const normalizeCrossDomainPattern = (pattern) => {
  if (typeof pattern !== "string") return null
  let working = pattern
  let type = "regex"
  if (working.startsWith("^") && working.endsWith("$")) {
    working = working.slice(1, -1)
    type = "exact"
  } else if (working.startsWith("^")) {
    working = working.slice(1)
    type = "startsWith"
  } else if (working.endsWith("$")) {
    working = working.slice(0, -1)
    type = "endsWith"
  } else if (working.includes("\\.")) {
    type = "contains"
  } else if (working.includes(".")) {
    type = "contains"
  }
  const cleaned = working
    .replace(/\\\\/g, "\\")
    .replace(/\\\./g, ".")
    .toLowerCase()
  return { type, value: cleaned, raw: pattern }
}

const collectCrossDomainPatternsFromRows = (rows) => {
  const patterns = []
  const seen = new Set()
  if (!Array.isArray(rows)) return patterns
  for (const row of rows) {
    if (!row || row.function !== "__ogt_cross_domain") continue
    for (const [key, value] of Object.entries(row)) {
      if (key === "function") continue
      const candidates = extractCrossDomainPatternValues(value)
      for (const candidate of candidates) {
        const normalized = normalizeCrossDomainPattern(candidate)
        if (!normalized) continue
        const dedupeKey = `${normalized.type}|${normalized.value}`
        if (seen.has(dedupeKey)) continue
        seen.add(dedupeKey)
        patterns.push(normalized)
      }
    }
  }
  return patterns
}

export const extractCrossDomainPatternsFromContents = (contents) => {
  const parsed = parseGaContainerContents(contents)
  if (!parsed) return []
  const rows = [
    ...(Array.isArray(parsed.macros) ? parsed.macros : []),
    ...(Array.isArray(parsed.tags) ? parsed.tags : []),
  ]
  return collectCrossDomainPatternsFromRows(rows)
}

export const patternMatchesHostname = (pattern, hostname) => {
  if (!pattern || !hostname) return false
  const host = hostname.toLowerCase()
  switch (pattern.type) {
    case "exact":
      return host === pattern.value
    case "startsWith":
      return host.startsWith(pattern.value)
    case "endsWith":
      return host.endsWith(pattern.value)
    case "contains":
      return host.includes(pattern.value)
    default:
      try {
        const regex = new RegExp(pattern.raw, "i")
        return regex.test(host)
      } catch (err) {
        console.warn("[GA4] Invalid cross-domain regex", {
          pattern: pattern.raw,
          error: err,
        })
        return host.includes(pattern.value)
      }
  }
}

export const isExpectedHostnameEntry = (entry) => {
  const value = entry?.expected
  if (typeof value === "boolean") return value
  if (typeof value === "number")
    return Number.isFinite(value) ? value !== 0 : false
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase()
    if (!normalized) return false
    return ["true", "yes", "expected"].includes(normalized)
  }
  return false
}

export const normalizeEventOwner = (value) => {
  if (typeof value !== "string") return ""
  return value.trim().toLowerCase()
}

export const createFlaggedPairKey = (hostname, eventOwner) => {
  const normalizedHost = normalizeHostnameText(hostname)
  const normalizedOwner = normalizeEventOwner(eventOwner)
  if (!normalizedHost || !normalizedOwner) return null
  return `${normalizedHost}|${normalizedOwner}`
}

export const loadFlaggedHostnameOwnerPairs = async (supabaseClient) => {
  const empty = { pairSet: new Set(), hostSet: new Set() }
  if (!supabaseClient) return empty
  try {
    const { data, error } = await supabaseClient
      .from("ga4_hostname_owner_flags")
      .select("hostname, event_owner")
    if (error) throw error
    const pairSet = new Set()
    const hostSet = new Set()
    for (const row of data || []) {
      const hostKey = normalizeHostnameText(row?.hostname)
      if (!hostKey) continue
      const ownerKey = normalizeEventOwner(row?.event_owner)
      if (ownerKey) pairSet.add(`${hostKey}|${ownerKey}`)
      else hostSet.add(hostKey)
    }
    return { pairSet, hostSet }
  } catch (error) {
    console.error("[GA4] Failed to load hostname owner flags", error)
    return empty
  }
}

const getEventOwnersForEntry = (entry) => {
  if (Array.isArray(entry?.eventOwners)) return entry.eventOwners
  if (Array.isArray(entry?.event_owners)) return entry.event_owners
  if (typeof entry?.event_owner === "string") return [entry.event_owner]
  return []
}

export const createCrossDomainCoverageEvaluator = ({
  supabaseClient,
  selectedOrgId = null,
  userId = null,
} = {}) => {
  let flaggedCache = null
  let flaggedPromise = null

  const ensureFlaggedPairs = async (provided) => {
    if (provided) return provided
    if (flaggedCache) return flaggedCache
    if (!flaggedPromise) {
      flaggedPromise = loadFlaggedHostnameOwnerPairs(supabaseClient).then(
        (result) => {
          flaggedCache = result
          flaggedPromise = null
          return result
        },
      )
    }
    return flaggedPromise
  }

  return async ({
    propertyRecord,
    accountId,
    hostnames,
    normalizedMeasurementIds = [],
    flaggedPairs = null,
  } = {}) => {
    if (!Array.isArray(hostnames) || hostnames.length === 0) {
      return { missingHosts: [] }
    }

    const expectedEntries = hostnames.filter(isExpectedHostnameEntry)
    if (expectedEntries.length === 0) return { missingHosts: [] }

    const organizationId =
      propertyRecord?.organization_id ?? selectedOrgId ?? null
    const resolvedUserId = userId || propertyRecord?.user_id || null
    if (!resolvedUserId) return { missingHosts: [] }

    const flaggedData = await ensureFlaggedPairs(flaggedPairs)
    const flaggedPairSet = flaggedData?.pairSet ?? new Set()
    const flaggedHostSet = flaggedData?.hostSet ?? new Set()

    const urlSet = new Set()
    const hostDisplayMap = new Map()
    const hostOwnersMap = new Map()
    const normalizedEntries = []

    for (const entry of expectedEntries) {
      const originalHost =
        typeof entry?.hostname === "string" ? entry.hostname : ""
      const normalizedHost = normalizeHostnameText(originalHost)
      if (!normalizedHost) continue
      const normalizedUrl = normalizeUrl(originalHost || normalizedHost)
      if (!normalizedUrl) continue
      urlSet.add(normalizedUrl)
      if (!hostDisplayMap.has(normalizedHost)) {
        hostDisplayMap.set(
          normalizedHost,
          originalHost && originalHost.trim()
            ? originalHost.trim()
            : normalizedHost,
        )
      }
      const normalizedOwners = getEventOwnersForEntry(entry)
        .map((owner) => normalizeEventOwner(owner))
        .filter(Boolean)
      if (hostOwnersMap.has(normalizedHost)) {
        const existing = hostOwnersMap.get(normalizedHost)
        for (const owner of normalizedOwners) existing.add(owner)
      } else {
        hostOwnersMap.set(normalizedHost, new Set(normalizedOwners))
      }
      normalizedEntries.push({
        entry,
        normalizedHost,
        displayHost:
          originalHost && originalHost.trim()
            ? originalHost.trim()
            : normalizedHost,
      })
    }

    if (urlSet.size === 0) return { missingHosts: [] }

    let websiteQuery = supabaseClient
      .from("website_urls")
      .select("id, url")
      .in("url", Array.from(urlSet))

    if (accountId) websiteQuery = websiteQuery.eq("account_id", accountId)
    if (organizationId)
      websiteQuery = websiteQuery.eq("organization_id", organizationId)
    else websiteQuery = websiteQuery.eq("user_id", resolvedUserId)

    const { data: websiteRows, error: websiteError } = await websiteQuery
    if (websiteError) {
      console.error(
        "[GA4] Failed to load websites for cross-domain validation",
        websiteError,
      )
      return { missingHosts: [] }
    }

    const websiteIds = (websiteRows || [])
      .map((row) => row?.id)
      .filter(Boolean)
    if (websiteIds.length === 0) return { missingHosts: [] }

    let containerQuery = supabaseClient
      .from("ga4_container_contents")
      .select("website_id, ga_id, contents")
      .in("website_id", websiteIds)

    if (Array.isArray(normalizedMeasurementIds) && normalizedMeasurementIds.length > 0) {
      containerQuery = containerQuery.in("ga_id", normalizedMeasurementIds)
    }

    if (organizationId)
      containerQuery = containerQuery.eq("organization_id", organizationId)
    else containerQuery = containerQuery.eq("user_id", resolvedUserId)

    const { data: containerRows, error: containerError } = await containerQuery
    if (containerError) {
      console.error(
        "[GA4] Failed to load GA4 container contents for cross-domain validation",
        containerError,
      )
      return { missingHosts: [] }
    }

    const patterns = []
    for (const row of containerRows || []) {
      const contents = typeof row?.contents === "string" ? row.contents : ""
      if (!contents) continue
      const entries = extractCrossDomainPatternsFromContents(contents)
      if (entries.length) patterns.push(...entries)
    }

    const missingHosts = []
    const evaluated = new Set()
    const hasPatterns = patterns.length > 0

    for (const { normalizedHost, displayHost } of normalizedEntries) {
      if (!normalizedHost || evaluated.has(normalizedHost)) continue
      evaluated.add(normalizedHost)

      if (flaggedHostSet.has(normalizedHost)) continue

      const owners = hostOwnersMap.get(normalizedHost)
      if (owners && owners.size > 0) {
        const allFlagged = Array.from(owners).every((owner) =>
          flaggedPairSet.has(`${normalizedHost}|${owner}`),
        )
        if (allFlagged) continue
      }

      const matched = hasPatterns
        ? patterns.some((pattern) =>
            patternMatchesHostname(pattern, normalizedHost),
          )
        : false
      if (!matched) {
        missingHosts.push(hostDisplayMap.get(normalizedHost) || displayHost)
      }
    }

    return { missingHosts }
  }
}

export const normalizeMeasurementId = (value) => {
  if (typeof value !== "string") return null
  const trimmed = value.trim().toUpperCase()
  if (!trimmed) return null
  return GA4_MEASUREMENT_ID_PATTERN.test(trimmed) ? trimmed : null
}
